import { useCallback, useEffect, useRef } from 'react';
import { createAriaHider } from './create-aria-hider';
import { getTabbableCandidates } from './get-tabbable-candidates';

export function useRovingFocus<T extends HTMLElement = HTMLDivElement>(active = true): (node: T | null) => void {
  const ref = useRef<T | null>();
  const restoreAria = useRef<Function>();
  const restoreFocusElement = useRef<HTMLElement>();

  const restoreFocus = useCallback(() => {
    if (restoreFocusElement.current) {
      restoreFocusElement.current.focus();
    }
  }, []);

  const processNode = useCallback((node: T) => {
    const children = getTabbableCandidates(node);
    const focusElement = children[0];
    const lastFocusElement = document.activeElement;

    if (lastFocusElement instanceof HTMLElement) {
      restoreFocusElement.current = lastFocusElement;
    }

    focus(focusElement);
  }, []);

  const setRef = useCallback(
    (node: T | null) => {
      if (!active) {
        return;
      }

      if (node === null) {
        return;
      }

      restoreAria.current = createAriaHider(node);
      if (ref.current === node) {
        return;
      }

      if (node) {
        // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.
        setTimeout(() => {
          if (node.getRootNode()) {
            processNode(node);
          }
        });

        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active, processNode],
  );

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab' && ref.current) {
      handleTab(ref.current, event);
    }
  }

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      if (restoreAria.current) {
        restoreAria.current();
        restoreFocus();
      }
    };
  }, [active, restoreFocus]);

  return setRef;
}

export function handleTab(node: HTMLElement, event: KeyboardEvent) {
  const tabbable = getTabbableCandidates(node);

  if (!tabbable.length) {
    event.preventDefault();
    return;
  }

  const finalTabbable = tabbable[event.shiftKey ? 0 : tabbable.length - 1];
  const root = node.getRootNode() as unknown as DocumentOrShadowRoot;
  const leavingFinalTabbable = finalTabbable === root.activeElement || node === root.activeElement;

  if (!leavingFinalTabbable) {
    return;
  }

  event.preventDefault();

  const target = tabbable[event.shiftKey ? tabbable.length - 1 : 0];

  if (target) {
    target.focus();
  }
}

export type FocusableTarget = HTMLElement | { focus(): void };

export function focus(element?: FocusableTarget | null, { select = false } = {}) {
  // only focus if that element is focusable
  if (element && element.focus) {
    const previouslyFocusedElement = document.activeElement;

    // NOTE: we prevent scrolling on focus, to minimize jarring transitions for users
    element.focus({ preventScroll: true });

    // only select if its not the same element, it supports selection and we need to select
    if (element !== previouslyFocusedElement && isSelectableInput(element) && select) {
      element.select();
    }
  }
}

/**
 * Checks if the given element is an input element that can be selected.
 */
export function isSelectableInput(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  element: any,
): element is FocusableTarget & { select: () => void } {
  return element instanceof HTMLInputElement && 'select' in element;
}
